/**
 * AppJobTable Component
 *
 * Displays a table of appJobs with actions (view, toggle, delete).
 */

import React from 'react';
import {
  ClockIcon,
  EyeIcon,
  TrashIcon,
  PlayIcon,
  PauseIcon,
  PencilIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui/Button';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import type { AppJobSanitized } from '../../../types/app-job.types';
import { AppJobStatus } from '../../../types/app-job.types';
import { formatRelativeTime } from '../../../lib/utils/format';
import { describeCronExpression } from '../../../lib/validators/app-job';

interface AppJobTableProps {
  appJobs: AppJobSanitized[];
  isLoading?: boolean;
  onView: (appJob: AppJobSanitized) => void;
  onEdit?: (appJob: AppJobSanitized) => void;
  onDelete?: (appJob: AppJobSanitized) => void;
  onPause?: (appJob: AppJobSanitized) => void;
  onResume?: (appJob: AppJobSanitized) => void;
  onTrigger?: (appJob: AppJobSanitized) => void;
  /** Permission to edit appJobs */
  canEdit?: boolean;
  /** Permission to delete appJobs */
  canDelete?: boolean;
  /** Permission to trigger appJobs */
  canTrigger?: boolean;
}

/**
 * Get status badge styling
 */
function getStatusBadge(status: AppJobStatus): { className: string; label: string } {
  switch (status) {
    case AppJobStatus.RUNNING:
      return { className: 'bg-green-100 text-green-800', label: 'Running' };
    case AppJobStatus.PAUSED:
      return { className: 'bg-yellow-100 text-yellow-800', label: 'Paused' };
    case AppJobStatus.DISABLED:
      return { className: 'bg-gray-100 text-gray-800', label: 'Disabled' };
    default:
      return { className: 'bg-gray-100 text-gray-800', label: status };
  }
}

/**
 * Get last run status badge styling
 */
function getLastRunBadge(status: 'completed' | 'failed' | undefined): { className: string; label: string } | null {
  if (!status) return null;
  switch (status) {
    case 'completed':
      return { className: 'bg-green-100 text-green-800', label: 'Success' };
    case 'failed':
      return { className: 'bg-red-100 text-red-800', label: 'Failed' };
    default:
      return null;
  }
}

/**
 * AppJobTable component
 */
export const AppJobTable: React.FC<AppJobTableProps> = ({
  appJobs,
  isLoading,
  onView,
  onEdit,
  onDelete,
  onPause,
  onResume,
  onTrigger,
  canEdit,
  canDelete,
  canTrigger,
}) => {
  if (isLoading) {
    return <TableSkeleton rows={5} columns={7} />;
  }

  if (appJobs.length === 0) {
    return (
      <EmptyState
        icon={<ClockIcon className="mx-auto h-12 w-12" />}
        title="No app jobs found"
        description="Create a appJob to run functions on a recurring basis."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Schedule
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Application
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Function
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Cron
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Status
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Last Run
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {appJobs.map(appJob => {
            const statusBadge = getStatusBadge(appJob.status);
            const lastRunBadge = getLastRunBadge(appJob.lastRunStatus);

            return (
              <tr key={appJob.appJobId} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <ClockIcon className="h-5 w-5 text-primary-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{appJob.appJobName}</div>
                      {appJob.description && (
                        <div className="text-xs text-gray-500 max-w-xs truncate">
                          {appJob.description}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {appJob.applicationCode}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{appJob.functionName}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">
                    {describeCronExpression(appJob.cronExpression)}
                  </div>
                  <div className="text-xs text-gray-500">{appJob.timezone}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.className}`}
                  >
                    {statusBadge.label}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {appJob.lastRunAt ? (
                    <div>
                      <div className="text-sm text-gray-500">
                        {formatRelativeTime(appJob.lastRunAt)}
                      </div>
                      {lastRunBadge && (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${lastRunBadge.className} mt-1`}
                        >
                          {lastRunBadge.label}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">Never</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onView(appJob)}
                      title="View Details"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </Button>
                    {canTrigger && onTrigger && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onTrigger(appJob)}
                        title="Run Now"
                      >
                        <BoltIcon className="h-4 w-4" />
                      </Button>
                    )}
                    {canEdit && onEdit && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onEdit(appJob)}
                        title="Edit App Job"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                    )}
                    {canEdit && appJob.status === AppJobStatus.RUNNING && onPause && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onPause(appJob)}
                        title="Pause Schedule"
                      >
                        <PauseIcon className="h-4 w-4" />
                      </Button>
                    )}
                    {canEdit && appJob.status === AppJobStatus.PAUSED && onResume && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onResume(appJob)}
                        title="Resume Schedule"
                      >
                        <PlayIcon className="h-4 w-4" />
                      </Button>
                    )}
                    {canDelete && onDelete && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => onDelete(appJob)}
                        title="Delete App Job"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
